import React from "react";
import { View, StyleSheet } from "react-native";
import { useTheme } from "@/hooks/useTheme";
import { ThemedText } from "@/components/ThemedText";
import type { BillingRateType, PayrollRateType } from "./types";

// RateBar accepts a single value prop (the rates object) like TimeBar
export type RateBarValueProps = {
  value: any; // The rates object from the cell
};

// Check if rates are equal
function ratesEqual(
  a: BillingRateType | PayrollRateType | null | undefined,
  b: BillingRateType | PayrollRateType | null | undefined,
): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return JSON.stringify(a) === JSON.stringify(b);
}

function formatBillingRate(rate: BillingRateType | null | undefined): string {
  if (!rate) return "-";
  if (rate.rate === "flat") return `$${rate.amount}`;
  return `$${rate.amount}/${rate.rate === "hourly" ? "hr" : "day"}`;
}

function formatPayrollRate(rate: PayrollRateType | null | undefined): string {
  if (!rate) return "-";
  if (rate.rate === "base_rate") return `${rate.multiplier}x (legacy)`;
  if (rate.rate === "base_add") return `+$${rate.offset}`;
  if (rate.rate === "flat") return `$${(rate as any).amount}`;
  return `$${(rate as any).amount}/${rate.rate === "hourly" ? "hr" : "day"}`;
}

export function RateBar({ value }: RateBarValueProps) {
  const { colors } = useTheme();

  // Parse value if it's a JSON string
  const ratesData =
    typeof value === "string"
      ? (() => {
          try {
            return JSON.parse(value);
          } catch {
            return {};
          }
        })()
      : value || {};

  const billingRate = ratesData.billing_rate || null;
  const payrollRate = ratesData.payroll_rate || null;
  const billingRateSnapshot = ratesData.billing_rate_snapshot || null;
  const payrollRateSnapshot = ratesData.payroll_rate_snapshot || null;

  const billingModified = !ratesEqual(billingRate, billingRateSnapshot);
  const payrollModified = !ratesEqual(payrollRate, payrollRateSnapshot);
  const anyModified = billingModified || payrollModified;

  return (
    <View style={styles.container}>
      {/* Billing Rate */}
      <View style={styles.rateItem}>
        <ThemedText style={[styles.rateLabel, { color: colors.placeholder }]}>Bill:</ThemedText>
        <ThemedText style={[styles.rateValue, billingModified && { color: colors.amber.text }]}>
          {formatBillingRate(billingRate)}
        </ThemedText>
      </View>

      {/* Payroll Rate */}
      <View style={styles.rateItem}>
        <ThemedText style={[styles.rateLabel, { color: colors.placeholder }]}>Pay:</ThemedText>
        <ThemedText style={[styles.rateValue, payrollModified && { color: colors.amber.text }]}>
          {formatPayrollRate(payrollRate)}
        </ThemedText>
      </View>

      {/* Modified indicator dot */}
      {anyModified && <View style={[styles.modifiedDot, { backgroundColor: colors.amber.border }]} />}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    flex: 1,
  },
  rateItem: {
    flexDirection: "row",
    alignItems: "center",
    gap: 2,
  },
  rateLabel: {
    fontSize: 11,
  },
  rateValue: {
    fontSize: 12,
    fontWeight: "500",
  },
  modifiedDot: {
    width: 6,
    height: 6,
    borderRadius: 3,
    marginLeft: "auto",
  },
});
